/**
 * @sw-package b2b
 */

import OffCanvas from 'src/plugin/offcanvas/offcanvas.plugin';
import Plugin from 'src/plugin-system/plugin.class';
import ElementLoadingIndicatorUtil from 'src/utility/loading-indicator/element-loading-indicator.util';
import { asRecord, isRecord } from '../../helper/record.helper';
import { showBootstrapToast } from '../../helper/bootstrap-toast.helper';
import B2bQuoteHistoryItemPlugin, { SOURCE_HISTORY_IDS_FIELD } from './quote-history-item.plugin';
import type { HistoryEntry, QuoteHistoryItemOptions } from './quote-history-item.plugin';

type Options = QuoteHistoryItemOptions & {
    contentSelector?: string,
    messageFormSelector?: string,
    offcanvasHistory?: string,
    historyItemPluginSelector?: string,
    historyListSelector?: string,
    historyLoadingSelector?: string,
    historyEmptySelector?: string,
    historyErrorSelector?: string,
    historyViewportSelector?: string,
    historyUrl?: string | null,
    historyReadUrl?: string | null,
    messageTextareaSelector?: string,
    messageSubmitButtonSelector?: string,
};

type HistoryPayload = {
    histories: HistoryEntry[],
    mergedHistoryIds: Record<string, string[]>,
    unreadHistoryIds: string[],
};

const historyPayloadCache = new Map<string, HistoryPayload>();
const historyPayloadRequests = new Map<string, Promise<HistoryPayload>>();

export default class B2bQuoteOffCanvasMessage extends Plugin {
    static options: Options = {
        contentSelector: '[data-off-canvas-message-content], [data-b2b-quote-offcanvas-message-content]',
        messageFormSelector: '[data-quote-history-message-form]',
        offcanvasHistory: '.quote-detail-offcanvas-history',
        historyItemPluginSelector: '[data-b2b-quote-history-item-plugin]',
        historyListSelector: '[data-quote-history-list]',
        historyLoadingSelector: '[data-quote-history-loading]',
        historyEmptySelector: '[data-quote-history-empty]',
        historyErrorSelector: '[data-quote-history-error]',
        historyViewportSelector: '.quote-detail-offcanvas__body, .offcanvas-body',
        messageTextareaSelector: 'textarea',
        messageSubmitButtonSelector: 'button[type="submit"]',
        historyUrl: null,
        historyReadUrl: null,
        currencyIsoCode: 'EUR',
        quoteSummary: null,
        statusLabels: {},
        snippets: {
            historyChangeFallback: 'Updated',
            historyDiscountAbsolute: 'Absolute %value%',
            historyDiscountRelative: 'Relative %value%%',
            historyOrderCreated: 'Order created',
            historySentQuoteValue: 'Quote #%quoteNumber%',
            merchantComment: 'Merchant',
            customerComment: 'You',
            quoteMessageSendError: 'Could not send quote message.',
            quoteHistoryMissingUrlError: 'Missing quote history URL.',
            quoteHistoryLoadError: 'Could not load quote history.',
            quoteHistoryReadError: 'Could not mark quote history as seen.',
        },
    };

    private el: HTMLElement;

    private activeMessageForm: HTMLFieldSetElement | null = null;

    private activeMessageFormElement: HTMLFormElement | null = null;

    private offCanvasHistory: HTMLElement | null = null;

    private static unregisterHistoryReadOnClose: (() => void) | null = null;

    private historyList: HTMLElement | null = null;

    private historyLoading: HTMLElement | null = null;

    private historyEmpty: HTMLElement | null = null;

    private historyError: HTMLElement | null = null;

    private readHistoryIds = new Set<string>();

    private seenHistoryIds = new Set<string>();

    private mergedHistoryIds: Record<string, string[]> = {};

    private unreadHistoryIds: string[] = [];

    private cachedHistoryPayload: HistoryPayload | null = null;

    private historyPayloadPromise: Promise<HistoryPayload> | null = null;

    init(): void {
        this.seenHistoryIds = new Set<string>();
        this._registerEventListeners();
        this._loadUnreadHistoryBadge();
    }

    _registerEventListeners(): void {
        this.el.addEventListener('click', this._onClickOffCanvasMessage.bind(this));
    }

    _onClickOffCanvasMessage(): void {
        const messageContent = document.querySelector(this.options.contentSelector);

        if (!messageContent) {
            return;
        }

        OffCanvas.open(
            messageContent.innerHTML,
            async () => {
                await window.PluginManager.initializePlugins();
                this._registerOffCanvasEvents();
                this._registerHistoryReadOnClose();
                this._registerHistoryViewportTracking();
                this._loadHistory();
            },
            'end',
            true,
            OffCanvas.REMOVE_OFF_CANVAS_DELAY(),
            false,
            'quote-detail-offcanvas-history'
        );
    }

    private _registerOffCanvasEvents(): void {
        this.offCanvasHistory = document.querySelector(this.options.offcanvasHistory) as HTMLElement;
        this._registerHistoryElements();

        const messageForm = this.offCanvasHistory?.querySelector(this.options.messageFormSelector) as HTMLFormElement;
        const messageFormFieldset = messageForm?.querySelector('fieldset') as HTMLFieldSetElement | null;

        if (!this.offCanvasHistory || !messageForm || !messageFormFieldset) {
            return;
        }

        this.activeMessageForm = messageFormFieldset;
        this._updateMessageSubmitButton();

        if (this.activeMessageFormElement === messageForm) {
            return;
        }

        this.activeMessageFormElement = messageForm;

        window.PluginManager.getPluginInstanceFromElement(
            messageForm,
            'FormAjaxSubmit'
        )?.$emitter.subscribe('onAfterAjaxSubmit', this._afterAjaxSubmitMessage.bind(this));
        messageForm.addEventListener('input', this._updateMessageSubmitButton.bind(this));
    }

    private _registerHistoryElements(): void {
        this.historyList = this.offCanvasHistory?.querySelector(this.options.historyListSelector) as HTMLElement | null;
        this.historyLoading = this.offCanvasHistory?.querySelector(this.options.historyLoadingSelector) as HTMLElement | null;
        this.historyEmpty = this.offCanvasHistory?.querySelector(this.options.historyEmptySelector) as HTMLElement | null;
        this.historyError = this.offCanvasHistory?.querySelector(this.options.historyErrorSelector) as HTMLElement | null;
    }

    private _afterAjaxSubmitMessage(): void {
        const textarea = this.activeMessageForm?.querySelector(this.options.messageTextareaSelector) as HTMLTextAreaElement;

        if (!textarea) {
            return;
        }

        textarea.value = '';
        this._updateMessageSubmitButton();
        this._loadHistory(true);
        this._handleMessageFormState();
        this._registerOffCanvasEvents();
    }

    private _handleMessageFormState(isDisabled: boolean = false): void {
        if (!this.activeMessageForm) {
            return;
        }

        this.activeMessageForm.disabled = isDisabled;
    }

    private _updateMessageSubmitButton(): void {
        const textarea = this.activeMessageForm?.querySelector(this.options.messageTextareaSelector) as HTMLTextAreaElement;
        const button = this.activeMessageForm?.querySelector(this.options.messageSubmitButtonSelector) as HTMLButtonElement;

        if (!button || !textarea) {
            return;
        }

        button.disabled = !textarea.value.trim();
    }

    private _getSnippet(key: keyof NonNullable<Options['snippets']>): string {
        return this.options.snippets?.[key] ?? B2bQuoteOffCanvasMessage.options.snippets?.[key] ?? '';
    }

    private _loadUnreadHistoryBadge(forceRefresh = false): Promise<void> {
        if (!this.options.historyUrl) {
            this._updateHistoryBadge();

            return Promise.resolve();
        }

        return this._loadHistoryPayload(forceRefresh).then((payload) => {
            this.unreadHistoryIds = payload.unreadHistoryIds;
            this._updateHistoryBadge(this.unreadHistoryIds.length);
        }).catch(() => {});
    }

    private _loadHistory(forceRefresh = false): void {
        this._setHistoryState('loading');

        if (!this.options.historyUrl) {
            this._setHistoryState('empty');
            return;
        }

        this._loadHistoryPayload(forceRefresh).then((payload) => {
            this._renderHistory(payload);
        }).catch((error) => {
            showBootstrapToast(error);
            this._setHistoryState('error');
        }).finally(() => {
            this._removeHistoryLoading();
        });
    }

    private _loadHistoryPayload(forceRefresh = false): Promise<HistoryPayload> {
        if (!this.options.historyUrl) {
            return Promise.reject(new Error(this._getSnippet('quoteHistoryMissingUrlError')));
        }

        const historyUrl = this.options.historyUrl;
        const sharedRequest = historyPayloadRequests.get(historyUrl);
        const sharedPayload = historyPayloadCache.get(historyUrl);

        if (!forceRefresh && sharedRequest) {
            return sharedRequest;
        }

        if (!forceRefresh && sharedPayload) {
            this.cachedHistoryPayload = sharedPayload;

            return Promise.resolve(sharedPayload);
        }

        if (!forceRefresh && this.historyPayloadPromise) {
            return this.historyPayloadPromise;
        }

        if (!forceRefresh && this.cachedHistoryPayload) {
            return Promise.resolve(this.cachedHistoryPayload);
        }

        const request = fetch(historyUrl, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then((response) => {
            if (!response.ok) {
                throw new Error(this._getSnippet('quoteHistoryLoadError'));
            }

            return response.json() as Promise<unknown>;
        }).then((payload) => {
            const historyPayload = this._extractHistoryPayload(payload);

            if (this.historyPayloadPromise === request) {
                this.cachedHistoryPayload = historyPayload;
            }
            historyPayloadCache.set(historyUrl, historyPayload);

            return historyPayload;
        }).finally(() => {
            if (this.historyPayloadPromise === request) {
                this.historyPayloadPromise = null;
            }
            if (historyPayloadRequests.get(historyUrl) === request) {
                historyPayloadRequests.delete(historyUrl);
            }
        });

        this.historyPayloadPromise = request;
        historyPayloadRequests.set(historyUrl, request);

        return request;
    }

    private _renderHistory(payload: HistoryPayload): void {
        if (!this.historyList) {
            return;
        }

        try {
            const historyItemPlugin = this._getHistoryItemPlugin();

            if (!historyItemPlugin) {
                this._setHistoryState('error');
                return;
            }

            historyItemPlugin.setRenderOptions({
                ...this.options,
                unreadHistoryIds: payload.unreadHistoryIds,
            });

            const histories = historyItemPlugin.mergeMerchantHistories(payload.histories);
            const historyElements = histories.flatMap((entry, index) => historyItemPlugin.render(
                entry,
                histories[index - 1],
                histories[index + 1],
            ));

            this.mergedHistoryIds = this._extendMergedHistoryIds(histories, payload.mergedHistoryIds);
            this.unreadHistoryIds = payload.unreadHistoryIds;
            this.historyList.replaceChildren(...historyElements);
            this._setHistoryState(histories.length > 0 ? 'ready' : 'empty');
            this._markVisibleUnreadHistoryItems();
        } catch {
            this._setHistoryState('error');
        }
    }

    private _getHistoryItemPlugin(): B2bQuoteHistoryItemPlugin | null {
        const element = this.offCanvasHistory?.querySelector(this.options.historyItemPluginSelector) as HTMLElement;

        if (!element) {
            return null;
        }

        const plugin = window.PluginManager.getPluginInstanceFromElement(
            element,
            'B2bQuoteHistoryItemPlugin'
        ) as B2bQuoteHistoryItemPlugin | undefined;

        if (
            !plugin
            || typeof plugin.render !== 'function'
            || typeof plugin.mergeMerchantHistories !== 'function'
            || typeof plugin.setRenderOptions !== 'function'
        ) {
            return null;
        }

        plugin.setRenderOptions(this.options);

        return plugin;
    }

    private _registerHistoryReadOnClose(): void {
        B2bQuoteOffCanvasMessage.unregisterHistoryReadOnClose?.();

        let hasMarkedHistoryAsSeen = false;
        const unregister = (): void => {
            this.offCanvasHistory?.removeEventListener('hide.bs.offcanvas', markHistoryAsSeen);
            this.offCanvasHistory?.removeEventListener('hidden.bs.offcanvas', clearSeenHistoryIds);
            window.removeEventListener('pagehide', markHistoryAsSeen);

            if (B2bQuoteOffCanvasMessage.unregisterHistoryReadOnClose === unregister) {
                B2bQuoteOffCanvasMessage.unregisterHistoryReadOnClose = null;
            }
        };
        const markHistoryAsSeen = (event?: Event): void => {
            if (hasMarkedHistoryAsSeen) {
                return;
            }

            hasMarkedHistoryAsSeen = true;
            this._markQuoteHistoryAsSeen();

            this.offCanvasHistory?.removeEventListener('hide.bs.offcanvas', markHistoryAsSeen);
            window.removeEventListener('pagehide', markHistoryAsSeen);

            if (event?.type === 'pagehide') {
                unregister();
            }
        };
        const clearSeenHistoryIds = (): void => {
            this.seenHistoryIds.clear();
            unregister();
        };

        B2bQuoteOffCanvasMessage.unregisterHistoryReadOnClose = unregister;

        this.offCanvasHistory?.addEventListener('hide.bs.offcanvas', markHistoryAsSeen);
        this.offCanvasHistory?.addEventListener('hidden.bs.offcanvas', clearSeenHistoryIds);
        window.addEventListener('pagehide', markHistoryAsSeen);
    }

    private _markQuoteHistoryAsSeen(): void {
        const historyIds = this._getUnreadHistoryIdsForRead();

        if (!this.options.historyReadUrl || historyIds.length === 0) {
            return;
        }

        historyIds.forEach((historyId) => {
            this.readHistoryIds.add(historyId);
        });

        const formData = new FormData();
        historyIds.forEach((historyId) => {
            formData.append('historyIds[]', historyId);
        });

        fetch(this.options.historyReadUrl, {
            method: 'post',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then((response) => {
            if (!response.ok) {
                throw new Error(this._getSnippet('quoteHistoryReadError'));
            }

        }).then(() => {
            return this._loadUnreadHistoryBadge(true);
        }).catch((error) => {
            showBootstrapToast(error);
            historyIds.forEach((historyId) => {
                this.readHistoryIds.delete(historyId);
            });
        });
    }

    private _getUnreadHistoryIdsForRead(): string[] {
        const unreadHistoryIds = new Set(this.unreadHistoryIds);

        return Array.from(new Set(Array.from(this.seenHistoryIds).flatMap((historyId) => {
            const visibleHistoryIds = [
                historyId,
                ...(this.mergedHistoryIds[historyId] ?? []),
            ];

            if (!visibleHistoryIds.some((visibleHistoryId) => unreadHistoryIds.has(visibleHistoryId))) {
                return [];
            }

            return visibleHistoryIds;
        }))).filter((historyId) => !this.readHistoryIds.has(historyId));
    }

    private _extendMergedHistoryIds(
        histories: HistoryEntry[],
        mergedHistoryIds: Record<string, string[]>
    ): Record<string, string[]> {
        return histories.reduce<Record<string, string[]>>((result, entry) => {
            const historyId = this._getRepresentativeHistoryId(entry);

            if (!historyId) {
                return result;
            }

            const sourceHistoryIds = this._getSourceHistoryIds(entry)
                .flatMap((sourceHistoryId) => result[sourceHistoryId] ?? [sourceHistoryId]);

            result[historyId] = Array.from(new Set([
                historyId,
                ...(result[historyId] ?? []),
                ...sourceHistoryIds,
            ]));

            return result;
        }, { ...mergedHistoryIds });
    }

    private _getSourceHistoryIds(entry: HistoryEntry): string[] {
        const sourceHistoryIds = entry[SOURCE_HISTORY_IDS_FIELD];

        if (Array.isArray(sourceHistoryIds)) {
            return sourceHistoryIds.filter((historyId): historyId is string => typeof historyId === 'string');
        }

        const historyId = this._getRepresentativeHistoryId(entry);

        return historyId ? [historyId] : [];
    }

    private _getRepresentativeHistoryId(entry: HistoryEntry): string {
        return typeof entry.id === 'string' ? entry.id : '';
    }

    private _registerHistoryViewportTracking(): void {
        if(!this.offCanvasHistory) {
            return;
        }

        const viewport = this._getHistoryViewport();
        const markVisibleItems = (): void => {
            this._markVisibleUnreadHistoryItems();
        };
        const unregister = (): void => {
            viewport?.removeEventListener('scroll', markVisibleItems);
            this.offCanvasHistory?.removeEventListener('scroll', markVisibleItems);
            window.removeEventListener('resize', markVisibleItems);
        };

        viewport?.addEventListener('scroll', markVisibleItems, { passive: true });
        this.offCanvasHistory?.addEventListener('scroll', markVisibleItems, { passive: true });
        window.addEventListener('resize', markVisibleItems);
        this.offCanvasHistory?.addEventListener('hide.bs.offcanvas', unregister, { once: true });
    }

    private _markVisibleUnreadHistoryItems(): void {
        const viewportRect = this._getHistoryViewportRect();

        this.offCanvasHistory?.querySelectorAll<HTMLElement>('[data-unseen="true"][data-history-id]').forEach((item) => {
            const historyId = item.dataset.historyId;

            if (!historyId || !this._isElementInsideViewport(item, viewportRect)) {
                return;
            }

            this.seenHistoryIds.add(historyId);
        });
    }

    private _getHistoryViewportRect(): DOMRect | Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'> {
        const viewport = this._getHistoryViewport();

        if (viewport) {
            return viewport.getBoundingClientRect();
        }

        return {
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
            left: 0,
        };
    }

    private _getHistoryViewport(): HTMLElement | null {
        return this.offCanvasHistory?.querySelector<HTMLElement>(this.options.historyViewportSelector);
    }

    private _isElementInsideViewport(
        element: HTMLElement,
        viewportRect: Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>
    ): boolean {
        const elementRect = element.getBoundingClientRect();
        const unseenIndicatorTop = elementRect.top + 11;
        const unseenIndicatorBottom = unseenIndicatorTop + 8;

        return unseenIndicatorTop >= viewportRect.top
            && unseenIndicatorBottom <= viewportRect.bottom
            && elementRect.right > viewportRect.left
            && elementRect.left < viewportRect.right;
    }

    private _updateHistoryBadge(count: number = 0): void {
        this.el.classList.toggle('hide-badge', count <= 0);
    }

    private _extractHistoryPayload(payload: unknown): HistoryPayload {
        return {
            histories: this._extractHistories(payload),
            mergedHistoryIds: this._extractMergedHistoryIds(payload),
            unreadHistoryIds: this._extractUnreadHistoryIds(payload),
        };
    }

    private _extractMergedHistoryIds(payload: unknown): Record<string, string[]> {
        const response = asRecord(payload);
        const data = asRecord(response.data);
        const object = asRecord(response.object);
        const mergedHistoryIds = asRecord(
            response.mergedHistoryIds
            ?? data.mergedHistoryIds
            ?? object.mergedHistoryIds
        );

        return Object.entries(mergedHistoryIds).reduce<Record<string, string[]>>((result, [historyId, mergedIds]) => {
            if (!Array.isArray(mergedIds)) {
                return result;
            }

            result[historyId] = mergedIds.filter((mergedId): mergedId is string => typeof mergedId === 'string');

            return result;
        }, {});
    }

    private _extractUnreadHistoryIds(payload: unknown): string[] {
        const response = asRecord(payload);
        const data = asRecord(response.data);
        const object = asRecord(response.object);
        const unreadHistoryIds = response.unreadHistoryIds
            ?? data.unreadHistoryIds
            ?? object.unreadHistoryIds;

        if (!Array.isArray(unreadHistoryIds)) {
            return [];
        }

        return unreadHistoryIds.filter((historyId): historyId is string => typeof historyId === 'string');
    }

    private _extractHistories(payload: unknown): HistoryEntry[] {
        const response = asRecord(payload);
        const data = asRecord(response.data);
        const object = asRecord(response.object);
        const histories = response.histories
            ?? data.histories
            ?? object.histories
            ?? response.elements;

        return this._collectionToArray(histories);
    }

    private _collectionToArray(collection: unknown): HistoryEntry[] {
        if (Array.isArray(collection)) {
            return collection.filter((entry): entry is HistoryEntry => isRecord(entry));
        }

        const record = asRecord(collection);
        const elements = record.elements ?? record.entities;

        if (Array.isArray(elements)) {
            return elements.filter((entry): entry is HistoryEntry => isRecord(entry));
        }

        if (isRecord(elements)) {
            return Object.values(elements).filter((entry): entry is HistoryEntry => isRecord(entry));
        }

        if (isRecord(collection)) {
            return Object.values(collection).filter((entry): entry is HistoryEntry => isRecord(entry) && this._isHistoryEntry(entry));
        }

        return [];
    }

    private _setHistoryState(state: 'loading' | 'empty' | 'error' | 'ready'): void {
        if (!this.historyList && !this.historyLoading && !this.historyEmpty && !this.historyError) {
            return;
        }

        this.historyList?.classList.toggle('d-none', state !== 'ready');
        this.historyLoading?.classList.toggle('d-none', state !== 'loading');
        this.historyEmpty?.classList.toggle('d-none', state !== 'empty');
        this.historyError?.classList.toggle('d-none', state !== 'error');

        if (!this.historyLoading) {
            return;
        }

        if (state === 'loading') {
            ElementLoadingIndicatorUtil.create(this.historyLoading);

            return;
        }

        ElementLoadingIndicatorUtil.remove(this.historyLoading);
    }

    private _removeHistoryLoading(): void {
        if (!this.historyLoading) {
            return;
        }

        this.historyLoading.classList.add('d-none');
        ElementLoadingIndicatorUtil.remove(this.historyLoading);
    }

    private _isHistoryEntry(entry: HistoryEntry): boolean {
        return ['comment', 'changes', 'action', 'createdAt', 'sentAt'].some((field) => field in entry);
    }
}
